import React, { useState, ChangeEvent, KeyboardEvent, useEffect, createRef, useRef } from 'react';

import { ChatMessage } from '../common/chat/types';
import { useChat } from '../common/chat/api';
import { apiUrl } from '../common/api';

import { ChatTurn } from './ChatTurn';
import { ChatBar } from './ChatBar';

export type ChatBoxProps = {
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  location: string;
  observerRef?: React.RefObject<HTMLDivElement>;
  height?: string;
};

export default function ChatBox(props: ChatBoxProps) {
  const { messages, setMessages, height } = props;

  const [chatId, setChatId] = useState<string | undefined>();

  const chatContentRef = createRef<HTMLDivElement>();

  const barRef = useRef<HTMLTextAreaElement | null>(null);

  // old chat stuff was here
  const { chat, error, loading, awaiting, streaming, streamResponse, addMessage } = useChat(
    apiUrl,
    messages,
    setMessages,
  );

  useEffect(() => {
    if (chat && (!chatId || chat.uuid !== chatId)) {
      setChatId(chat.uuid);
    }
  }, [chat, chatId]);

  useEffect(() => {
    streamResponse();
  }, [awaiting, chat, streaming, streamResponse]);

  useEffect(() => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = chatContentRef.current?.scrollHeight;
    }
  }, [messages]);

  const adjustChatBoxHeight = () => {
    if (barRef.current && chatContentRef.current) {
      const barHeight = barRef.current.offsetHeight;
      const fullHeight = height ?? '600px';
      chatContentRef.current.style.height = `max(calc(${fullHeight} - ${barHeight}px), 200px)`;
    }
  };

  useEffect(() => {
    adjustChatBoxHeight();

    // Optionally, you can add a resize event listener to handle window resize
    window.addEventListener('resize', adjustChatBoxHeight);

    // Cleanup on component unmount
    return () => {
      window.removeEventListener('resize', adjustChatBoxHeight);
    };
  }, []); // Run effect when bar height changes

  return (
    <div className="chat-box">
      <div
        ref={chatContentRef}
        className="messages overflow-y-auto bg-[#101c2c] flex flex-col space-y-2 overflow-x-hidden"
      >
        {messages.map((item, index) => (
          <ChatTurn
            key={index}
            addMessage={(message: string) => addMessage(message, props.location)}
            index={index}
            length={messages.length}
            loading={loading || awaiting}
            message={item}
          />
        ))}
        <div ref={props.observerRef} className="h-1 bg-transparent" />
      </div>
      <ChatBar
        addMessage={addMessage}
        loading={loading || awaiting}
        location={props.location}
        textareaRef={barRef}
        onResize={adjustChatBoxHeight} // Callback to adjust height on resize
      />
    </div>
  );
}
